import { Box, Card, Divider, Stack, Typography } from '@mui/material';
import type { KeyboardEvent } from 'react';

import { DocumentIcon } from '../../../icons';
import { Avatar } from '../../atoms/Avatar';
import { ResourceMenuActions } from '../../organisms/ResourceMenuActions';
import { HTMLDisplay } from '../HTMLDisplay';
import {
  type EventCardAction,
  type EventCardProps,
  type EventItem,
  useEventCard,
} from './useEventCard';

type Props = EventCardProps;

export function EventCard(props: Props) {
  const hook = useEventCard(props);

  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (props.onClick && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      props.onClick();
    }
  }

  return (
    <Card
      variant="outlined"
      data-testid={props['data-testid']}
      onClick={props.onClick}
      onKeyDown={props.onClick ? handleKeyDown : undefined}
      tabIndex={props.onClick ? 0 : undefined}
      role={props.onClick ? 'button' : undefined}
      sx={(theme) => ({
        p: { xs: 2, sm: 2.5 },
        cursor: props.onClick ? 'pointer' : 'default',
        transition: theme.transitions.create(['box-shadow'], {
          duration: theme.transitions.duration.shorter,
        }),
        ...(props.onClick && {
          '&:hover': {
            backgroundColor: theme.palette.action.hover,
          },
        }),
      })}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={{ xs: 1.5, sm: 2.5 }}
        sx={{ alignItems: { sm: 'stretch' } }}
      >
        <Stack
          spacing={0.25}
          sx={{
            justifyContent: 'center',
            minWidth: { sm: 170 },
            flexShrink: 0,
          }}
        >
          <Typography
            variant="body2Emphasis"
            sx={{ color: hook.accentColor, lineHeight: 1.3 }}
          >
            {hook.dayName}
          </Typography>
          <Typography
            variant="body1Emphasis"
            sx={{ lineHeight: 1.25 }}
          >
            {hook.fullDate}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
          >
            {hook.timeText}
          </Typography>
        </Stack>

        <Divider
          orientation="vertical"
          flexItem
          sx={{ display: { xs: 'none', sm: 'block' } }}
        />
        <Divider
          orientation="horizontal"
          sx={{ display: { xs: 'block', sm: 'none' } }}
        />

        <Stack spacing={1} sx={{ flex: 1, minWidth: 0, justifyContent: 'center' }}>
          <Stack
            direction="row"
            spacing={1.5}
            sx={{
              alignItems: 'flex-start',
              justifyContent: 'space-between',
            }}
          >
            <Stack
              direction="row"
              spacing={1}
              sx={{ alignItems: 'center', flexWrap: 'wrap' }}
            >
              <Typography variant="body1Emphasis" component="span">
                {props.event.title}
              </Typography>
              {hook.providerName ? (
                <Typography
                  variant="body2Emphasis"
                  component="span"
                  sx={{ color: hook.accentColor }}
                >
                  {` avec ${hook.providerName}`}
                </Typography>
              ) : null}
            </Stack>

            {props.action ? (
              <Stack sx={{ flexShrink: 0 }}>{props.action}</Stack>
            ) : hook.hasActions ? (
              <Stack sx={{ flexShrink: 0 }}>
                <ResourceMenuActions
                  actions={hook.actions}
                  tooltip={hook.actionsButtonTooltip}
                  ariaLabel={hook.actionsButtonAriaLabel}
                />
              </Stack>
            ) : null}
          </Stack>

          {hook.description ? (
            <Stack
              direction="row"
              spacing={1}
              sx={{ alignItems: 'flex-start' }}
            >
              <Stack sx={{ color: 'text.secondary', mt: 0.25, flexShrink: 0 }}>
                <DocumentIcon fontSize="small" />
              </Stack>
              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                  color: 'text.secondary',
                  fontSize: '0.875rem',
                  lineHeight: 1.45,
                }}
              >
                {props.renderDescription ? (
                  props.renderDescription(hook.description)
                ) : (
                  <HTMLDisplay content={hook.description} />
                )}
              </Box>
            </Stack>
          ) : null}

          {hook.contactName ? (
            <Stack
              direction="row"
              spacing={1}
              sx={{ alignItems: 'center' }}
            >
              {hook.contactAvatarUrl ? (
                <Avatar
                  src={hook.contactAvatarUrl}
                  alt={hook.contactName}
                  size="small"
                />
              ) : (
                <Avatar
                  size="small"
                  alt={hook.contactName}
                >
                  {hook.contactName.slice(0, 1).toUpperCase()}
                </Avatar>
              )}
              <Typography variant="body2">
                {hook.contactName}
              </Typography>
            </Stack>
          ) : null}
        </Stack>
      </Stack>
    </Card>
  );
}

export type { EventCardAction, EventCardProps, EventItem };

